import { gql } from '../../../core/graphql-client.js';
import { router } from '../../../core/router.js';
import { toast } from '../../../core/event-bus.js';
import { confirmDialog } from '../../confirm-dialog.js';
import type { Person, Role, PersonRoleLimit, PersonExclusion } from '../../../types.js';

export class PersonEditPage extends HTMLElement {
  private person: Person | null = null;
  private roles: Role[] = [];
  private allPeople: Person[] = [];
  private selectedRoleSlugs = new Set<string>();
  private roleLimits = new Map<string, number>(); // roleSlug → maxPerQuarter
  private exclusions: PersonExclusion[] = [];

  get personId(): number | null {
    const id = this.getAttribute('id');
    return id ? Number(id) : null;
  }

  get isNew(): boolean { return this.personId === null; }

  connectedCallback(): void {
    this.innerHTML = '<loading-spinner text="Ładowanie..."></loading-spinner>';
    this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      const [rData, pplData] = await Promise.all([
        gql.query<{ roles: Role[] }>(`
          query { roles { slug displayName timeSlot isAutoScheduled sortOrder } }
        `),
        gql.query<{ people: Person[] }>(`
          query { people { id displayName } }
        `),
      ]);
      this.roles = rData.roles.sort((a, b) => a.sortOrder - b.sortOrder);
      this.allPeople = pplData.people;

      if (!this.isNew) {
        const pData = await gql.query<{ person: Person | null }>(`
          query($id: Int!) {
            person(id: $id) {
              id firstName lastName displayName email phone isActive isAdmin maxPerQuarter
              roles { slug }
              roleLimits { role { slug } maxPerQuarter }
              exclusions { otherPersonId otherPersonDisplayName exclusionType scope }
            }
          }
        `, { id: this.personId });

        this.person = pData.person;
        if (this.person?.roles) {
          this.selectedRoleSlugs = new Set(this.person.roles.map(r => r.slug));
        }
        if (this.person?.roleLimits) {
          for (const rl of this.person.roleLimits) {
            this.roleLimits.set(rl.role.slug, rl.maxPerQuarter);
          }
        }
        this.exclusions = this.person?.exclusions ?? [];
      }

      this.render();
    } catch (e) {
      this.innerHTML = `<div class="page"><p class="error-text">Błąd: ${e instanceof Error ? e.message : e}</p></div>`;
    }
  }

  private render(): void {
    const p = this.person;

    this.innerHTML = `
      <div class="page">
        <div class="page-header">
          <h1>${this.isNew ? 'Nowa osoba' : 'Edytuj osobę'}</h1>
          ${!this.isNew ? '<button class="btn btn-danger" id="deactivate-btn">Dezaktywuj</button>' : ''}
        </div>

        <form id="person-form">
          <div class="form-group">
            <label for="firstName">Imię</label>
            <input id="firstName" type="text" value="${p?.firstName ?? ''}" required>
          </div>
          <div class="form-group">
            <label for="lastName">Nazwisko</label>
            <input id="lastName" type="text" value="${p?.lastName ?? ''}" required>
          </div>
          <div class="form-group">
            <label for="email">Email</label>
            <input id="email" type="email" value="${p?.email ?? ''}">
          </div>
          <div class="form-group">
            <label for="phone">Telefon (+48...)</label>
            <input id="phone" type="tel" value="${p?.phone ?? ''}">
          </div>
          <div class="form-group">
            <label class="switch">
              <input type="checkbox" id="isAdmin" ${p?.isAdmin ? 'checked' : ''}>
              <span>Administrator</span>
            </label>
          </div>

          <div class="form-group">
            <label>Role</label>
            <div class="role-chips">
              ${this.roles.map(r => `
                <span class="chip ${this.selectedRoleSlugs.has(r.slug) ? 'active' : ''}" data-role-slug="${r.slug}">${r.displayName}</span>
              `).join('')}
            </div>
          </div>

          ${!this.isNew ? this.renderLimitsSection() : ''}
          ${!this.isNew ? this.renderExclusionsSection() : ''}

          <button type="submit" class="btn btn-primary form-submit">${this.isNew ? 'Utwórz' : 'Zapisz'}</button>
        </form>
      </div>
      <style>
        .role-chips { display: flex; flex-wrap: wrap; gap: var(--space-sm); }
        .form-submit { width: 100%; margin-top: var(--space-md); }
        .error-text { color: var(--color-error); }
        .limits-section, .exclusions-section {
          border-top: 1px solid var(--color-surface-container);
          padding-top: var(--space-md);
          margin-top: var(--space-md);
        }
        .limits-section h3, .exclusions-section h3 {
          margin: 0 0 var(--space-sm) 0;
          font-size: .9rem;
        }
        .limit-row {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          margin-bottom: var(--space-xs);
        }
        .limit-row label { flex: 1; font-size: .85rem; }
        .limit-row input {
          width: 60px;
          text-align: center;
          padding: 4px;
          font-size: .85rem;
        }
        .limit-suffix { font-size: .8rem; color: var(--color-on-surface-muted); }
        .exclusion-item {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          padding: var(--space-xs) 0;
          font-size: .85rem;
          border-bottom: 1px solid var(--color-surface-container);
        }
        .exclusion-item:last-child { border-bottom: none; }
        .exclusion-name { flex: 1; }
        .exclusion-badge {
          font-size: .7rem;
          padding: 2px 6px;
          border-radius: var(--radius-sm, 4px);
          text-transform: uppercase;
          font-weight: 600;
        }
        .exclusion-badge.hard { background: var(--color-error-bg, #fdecea); color: var(--color-error); }
        .exclusion-badge.soft { background: var(--color-warning-bg, #fff3cd); color: var(--color-warning, #856404); }
        .exclusion-scope { font-size: .75rem; color: var(--color-on-surface-muted); }
        .exclusion-remove {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          padding: 2px;
          color: var(--color-error);
        }
        .add-exclusion-row {
          display: flex;
          gap: var(--space-sm);
          margin-top: var(--space-sm);
          flex-wrap: wrap;
          align-items: center;
        }
        .add-exclusion-row select, .add-exclusion-row button {
          font-size: .85rem;
          padding: 4px 8px;
        }
      </style>
    `;

    // Role chip toggles
    this.querySelector('.role-chips')!.addEventListener('click', (e) => {
      const chip = (e.target as HTMLElement).closest('.chip') as HTMLElement | null;
      if (!chip) return;
      const slug = chip.dataset.roleSlug!;
      if (this.selectedRoleSlugs.has(slug)) {
        this.selectedRoleSlugs.delete(slug);
        chip.classList.remove('active');
      } else {
        this.selectedRoleSlugs.add(slug);
        chip.classList.add('active');
      }
      this.updateLimitsVisibility();
    });

    // Form submit
    this.querySelector('#person-form')!.addEventListener('submit', (e) => {
      e.preventDefault();
      this.save();
    });

    // Deactivate
    this.querySelector('#deactivate-btn')?.addEventListener('click', () => this.deactivate());

    // Exclusion remove buttons
    this.querySelectorAll('.exclusion-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const el = (e.target as HTMLElement).closest('[data-other-person-id]') as HTMLElement;
        if (!el) return;
        const otherPersonId = Number(el.dataset.otherPersonId);
        const scope = el.dataset.scope!;
        this.removeExclusion(otherPersonId, scope);
      });
    });

    // Add exclusion button
    this.querySelector('#add-exclusion-btn')?.addEventListener('click', () => this.addExclusion());
  }

  private renderLimitsSection(): string {
    const selectedRoles = this.roles.filter(r => this.selectedRoleSlugs.has(r.slug));
    return `
      <div class="limits-section">
        <h3>Limity przypisań na kwartał</h3>
        <div class="form-group">
          <div class="limit-row">
            <label for="maxPerQuarter">Łączny max na kwartał</label>
            <input id="maxPerQuarter" type="number" min="0" max="20"
                   value="${this.person?.maxPerQuarter ?? ''}" placeholder="—">
            <span class="limit-suffix">razy</span>
          </div>
        </div>
        <div id="role-limits-list">
          ${selectedRoles.map(r => `
            <div class="limit-row" data-limit-role="${r.slug}">
              <label>${r.displayName}</label>
              <input type="number" min="0" max="13" class="role-limit-input"
                     data-role-slug="${r.slug}"
                     value="${this.roleLimits.get(r.slug) ?? ''}" placeholder="—">
              <span class="limit-suffix">× / kwartał</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  private renderExclusionsSection(): string {
    const otherPeople = this.allPeople.filter(p => p.id !== this.personId);
    return `
      <div class="exclusions-section">
        <h3>Wykluczenia z innymi osobami</h3>
        <div id="exclusions-list">
          ${this.exclusions.map(ex => `
            <div class="exclusion-item" data-other-person-id="${ex.otherPersonId}" data-scope="${ex.scope}">
              <span class="exclusion-name">${ex.otherPersonDisplayName}</span>
              <span class="exclusion-badge ${ex.exclusionType.toLowerCase()}">${ex.exclusionType === 'HARD' ? 'twarde' : 'miękkie'}</span>
              <span class="exclusion-scope">${this.scopeLabel(ex.scope)}</span>
              <button type="button" class="exclusion-remove" title="Usuń">✕</button>
            </div>
          `).join('')}
          ${this.exclusions.length === 0 ? '<p class="exclusion-scope">Brak wykluczeń</p>' : ''}
        </div>
        <div class="add-exclusion-row">
          <select id="excl-person">
            <option value="">— wybierz osobę —</option>
            ${otherPeople.map(p => `<option value="${p.id}">${p.displayName}</option>`).join('')}
          </select>
          <select id="excl-type">
            <option value="hard">Twarde</option>
            <option value="soft">Miękkie</option>
          </select>
          <select id="excl-scope">
            <option value="any">Cały dzień</option>
            <option value="same_slot">Ten sam slot</option>
          </select>
          <button type="button" class="btn btn-secondary" id="add-exclusion-btn">Dodaj</button>
        </div>
      </div>
    `;
  }

  private scopeLabel(scope: string): string {
    switch (scope) {
      case 'any': return 'cały dzień';
      case 'same_slot': return 'ten sam slot';
      default: return scope;
    }
  }

  private updateLimitsVisibility(): void {
    const container = this.querySelector('#role-limits-list');
    if (!container) return;
    const selectedRoles = this.roles.filter(r => this.selectedRoleSlugs.has(r.slug));
    container.innerHTML = selectedRoles.map(r => `
      <div class="limit-row" data-limit-role="${r.slug}">
        <label>${r.displayName}</label>
        <input type="number" min="0" max="13" class="role-limit-input"
               data-role-slug="${r.slug}"
               value="${this.roleLimits.get(r.slug) ?? ''}" placeholder="—">
        <span class="limit-suffix">× / kwartał</span>
      </div>
    `).join('');
  }

  private async save(): Promise<void> {
    const firstName = (this.querySelector('#firstName') as HTMLInputElement).value.trim();
    const lastName = (this.querySelector('#lastName') as HTMLInputElement).value.trim();
    const email = (this.querySelector('#email') as HTMLInputElement).value.trim();
    const phone = (this.querySelector('#phone') as HTMLInputElement).value.trim();
    const isAdmin = (this.querySelector('#isAdmin') as HTMLInputElement).checked;
    const displayName = `${firstName} ${lastName[0]}.`;

    // Read max per quarter
    const maxInput = this.querySelector('#maxPerQuarter') as HTMLInputElement | null;
    const maxPerQuarter = maxInput?.value ? Number(maxInput.value) : null;

    try {
      if (this.isNew) {
        await gql.query(`
          mutation($input: CreatePersonInput!) {
            createPerson(input: $input) { id }
          }
        `, {
          input: {
            firstName, lastName, displayName,
            ...(email && { email }),
            ...(phone && { phone }),
            isAdmin,
            roleSlugs: [...this.selectedRoleSlugs],
          },
        });
        toast('Osoba utworzona', 'success');
      } else {
        // Update person data
        await gql.query(`
          mutation($id: Int!, $input: UpdatePersonInput!) {
            updatePerson(id: $id, input: $input) { id }
          }
        `, {
          id: this.personId,
          input: {
            firstName, lastName, displayName,
            email: email || null,
            phone: phone || null,
            isAdmin,
            maxPerQuarter,
          },
        });

        // Update roles
        await gql.query(`
          mutation($personId: Int!, $roleSlugs: [String!]!) {
            setPersonRoles(personId: $personId, roleSlugs: $roleSlugs) { id }
          }
        `, { personId: this.personId, roleSlugs: [...this.selectedRoleSlugs] });

        // Update role limits
        const limitInputs = this.querySelectorAll('.role-limit-input') as NodeListOf<HTMLInputElement>;
        for (const input of limitInputs) {
          const roleSlug = input.dataset.roleSlug!;
          const val = input.value ? Number(input.value) : 0;
          await gql.query(`
            mutation($personId: Int!, $roleSlug: String!, $max: Int!) {
              setPersonRoleLimit(personId: $personId, roleSlug: $roleSlug, maxPerQuarter: $max) { id }
            }
          `, { personId: this.personId, roleSlug, max: val });
        }

        toast('Zapisano', 'success');
      }
      router.navigate('/admin/people');
    } catch (e) {
      toast(`Błąd: ${e instanceof Error ? e.message : e}`, 'error');
    }
  }

  private async addExclusion(): Promise<void> {
    const personSelect = this.querySelector('#excl-person') as HTMLSelectElement;
    const typeSelect = this.querySelector('#excl-type') as HTMLSelectElement;
    const scopeSelect = this.querySelector('#excl-scope') as HTMLSelectElement;

    const otherPersonId = Number(personSelect.value);
    if (!otherPersonId) {
      toast('Wybierz osobę', 'error');
      return;
    }

    try {
      await gql.query(`
        mutation($input: PersonExclusionInput!) {
          setPersonExclusion(input: $input) { otherPersonId otherPersonDisplayName exclusionType scope }
        }
      `, {
        input: {
          personId: this.personId,
          otherPersonId,
          exclusionType: typeSelect.value,
          scope: scopeSelect.value,
        },
      });
      toast('Wykluczenie dodane', 'success');
      this.loadData();
    } catch (e) {
      toast(`Błąd: ${e instanceof Error ? e.message : e}`, 'error');
    }
  }

  private async removeExclusion(otherPersonId: number, scope: string): Promise<void> {
    const ok = await confirmDialog('Usuń wykluczenie', 'Usunąć to wykluczenie?', 'Usuń');
    if (!ok) return;

    try {
      await gql.query(`
        mutation($personId: Int!, $otherPersonId: Int!, $scope: String!) {
          removePersonExclusion(personId: $personId, otherPersonId: $otherPersonId, scope: $scope)
        }
      `, { personId: this.personId, otherPersonId, scope });
      toast('Wykluczenie usunięte', 'success');
      this.exclusions = this.exclusions.filter(e => !(e.otherPersonId === otherPersonId && e.scope === scope));
      this.render();
    } catch (e) {
      toast(`Błąd: ${e instanceof Error ? e.message : e}`, 'error');
    }
  }

  private async deactivate(): Promise<void> {
    const ok = await confirmDialog('Dezaktywuj', 'Dezaktywować tę osobę?', 'Dezaktywuj');
    if (!ok) return;
    try {
      await gql.query(`mutation($id: Int!) { deactivatePerson(id: $id) { id } }`, { id: this.personId });
      toast('Osoba dezaktywowana', 'success');
      router.navigate('/admin/people');
    } catch (e) {
      toast(`Błąd: ${e instanceof Error ? e.message : e}`, 'error');
    }
  }
}

customElements.define('person-edit-page', PersonEditPage);
